import { type AxiosError } from 'axios';
import TripPackageLoader, {
    type TripPackageLoaderResult,
} from '../TripPackageLoader';
import { type RoutePackageLoaderConfig } from './types';
import { RoutePackage } from './RoutePackage';
import {
    type GetAvailableIdType,
    JourneyApiApiGetAvailableGet,
    type JourneyApiApiGetAvailableGetRequest,
} from '@clients/MultiRouteApi';
import { ReserveType } from '@utils/itinerary';

export class RoutePackageLoader extends TripPackageLoader<RoutePackage> {
    private readonly _adultCount: number;
    private readonly _childCount: number;
    private readonly _infantCount: number;

    private currentServiceInLoad: GetAvailableIdType;

    constructor({
        originCode,
        destinationCode,
        date,
        adultCount,
        childCount,
        infantCount,
        url,
        originName,
        destinationName,
        reserveType,
        isRoundTrip,
    }: RoutePackageLoaderConfig) {
        super({
            totalSeatCount: adultCount + childCount,
            destinationCode,
            destinationName,
            originCode,
            originName,
            date,
            url,
            isRoundTrip,
        });
        this._adultCount = adultCount;
        this._childCount = childCount;
        this._infantCount = infantCount;
        switch (reserveType) {
            case ReserveType.Bus:
                this.currentServiceInLoad = 3;
                break;
            case ReserveType.Train:
                this.currentServiceInLoad = 2;
                break;
            case ReserveType.Flight:
                this.currentServiceInLoad = 1;
                break;
            default:
                throw new Error('reserve type is not supported');
        }
    }

    reserveType = ReserveType.Route;

    get adultCount() {
        return this._adultCount;
    }

    get childCount() {
        return this._childCount;
    }

    get infantCount() {
        return this._infantCount;
    }

    override compareTo(other: RoutePackageLoaderConfig): boolean {
        if (this._originCode !== other.originCode) {
            return false;
        }
        if (this._destinationCode !== other.destinationCode) {
            return false;
        }
        if (this.date.toString() !== other.date.toString()) {
            return false;
        }
        if (this._adultCount !== other.adultCount) {
            return false;
        }
        if (this._childCount !== other.childCount) {
            return false;
        }
        if (this._infantCount !== other.infantCount) {
            return false;
        }
        return true;
    }

    override async *_getPackages(
        axiosParams?: any
    ): AsyncGenerator<TripPackageLoaderResult<RoutePackage>> {
        try {
            const paramData: JourneyApiApiGetAvailableGetRequest = {
                originId: this._originCode,
                destinationId: this._destinationCode,
                date: dayConfig.gregory(this.date).format(formatter()),
                adultCount: this._adultCount,
                childCount: this._childCount,
                infantCount: this._infantCount,
                idType: this.currentServiceInLoad,
            };

            const { data } = await JourneyApiApiGetAvailableGet(
                paramData,
                axiosParams
            );

            const packages =
                data.journeys?.flatMap(journey => {
                    try {
                        return new RoutePackage(journey, {
                            adultCount: this.adultCount,
                            childCount: this.childCount,
                            infantCount: this.infantCount,
                        });
                    } catch (e) {
                        // trains with unsupported selltypes
                        if (e === 0) {
                            return [];
                        } else {
                            throw new Error(
                                'something went wrong in the packages mapper'
                            );
                        }
                    }
                }) ?? [];

            yield {
                list: [...packages],
                originLocation: null,
                destinationLocation: null,
                filterMetaData: {},
                metaData: {},
            };
        } catch (error) {
            const e = error as AxiosError;
            reportException(e);
            throw error;
        }
    }
}
